import { useCallback, useMemo, useState } from "react";

import { AssessmentStatusTable } from "./AssessmentStatusTable";
import { EmptyAssessmentDashboard } from "./EmptyAssessmentDashboard";
import { AssessmentStatusTableSortField } from "./hooks";
import {
  buildOpenForSubmissionCSVData,
  filterCSVColumns,
  mapAllAssessmentTableRow,
  partitionAllAssessmentDashboardRows,
  translateAllAssessmentReport,
} from "./utils";

import { AssessmentStatus } from "api/api-data-types";
import {
  serverGetAllAssessmentReport,
  serverGetAllAssessmentTable,
} from "api/api-server-assessment";
import { t } from "i18n/i18n";
import { useAppSelector } from "store/index";
import { selectCurrentSectionId } from "store/selectors";
import { usePromiseEffect } from "store/store-hooks";

const ASSESSABLE_HIDDEN_COLUMNS = new Set<AssessmentStatusTableSortField>([
  "due_date",
  "last_assessed",
]);
const ASSESSED_HIDDEN_COLUMNS = new Set<AssessmentStatusTableSortField>([
  "due_date",
  "last_submitted",
]);
const OPEN_FOR_SUBMISSIONS_HIDDEN_COLUMNS = new Set<AssessmentStatusTableSortField>([
  "status",
  "publishing",
  "submissions",
  "progress",
  "avg_points",
  "last_submitted",
  "last_assessed",
]);

export const AllAssessmentDashboard = () => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const [refetchKey, setRefetchKey] = useState(0);
  const refetch = useCallback(() => setRefetchKey((k) => k + 1), []);

  const { data: allAssessmentTableRows, isLoading } = usePromiseEffect(async () => {
    return sectionId ? serverGetAllAssessmentTable(sectionId) : null;
  }, [sectionId, refetchKey]);

  const allAssessmentsTableRows = useMemo(() => {
    if (!allAssessmentTableRows) {
      return [];
    }

    return allAssessmentTableRows.map(mapAllAssessmentTableRow);
  }, [allAssessmentTableRows]);

  const { openForSubmissionsRows, assessableRows, assessedRows } = useMemo(
    () => partitionAllAssessmentDashboardRows(allAssessmentsTableRows),
    [allAssessmentsTableRows],
  );

  const getAssessableExportData = useCallback(async () => {
    const csvData = await serverGetAllAssessmentReport({
      section_id: sectionId!,
      report_filter: AssessmentStatus.ASSESSABLE,
    });
    return translateAllAssessmentReport(filterCSVColumns(csvData, ASSESSABLE_HIDDEN_COLUMNS));
  }, [sectionId]);

  const getAssessedExportData = useCallback(async () => {
    const csvData = await serverGetAllAssessmentReport({
      section_id: sectionId!,
      report_filter: AssessmentStatus.ASSESSED,
    });
    return translateAllAssessmentReport(filterCSVColumns(csvData, ASSESSED_HIDDEN_COLUMNS));
  }, [sectionId]);

  const openForSubmissionsExportData = useMemo(
    () =>
      buildOpenForSubmissionCSVData(openForSubmissionsRows, OPEN_FOR_SUBMISSIONS_HIDDEN_COLUMNS),
    [openForSubmissionsRows],
  );

  if (!sectionId) {
    return null;
  }

  const hasNoData =
    !isLoading &&
    assessableRows.length === 0 &&
    assessedRows.length === 0 &&
    openForSubmissionsRows.length === 0;

  if (hasNoData) {
    return <EmptyAssessmentDashboard />;
  }

  return (
    <main className="assessment-dashboard">
      <AssessmentStatusTable
        defaultSortField="last_submitted"
        emptyDescription={t("assessment_dashboard.all.empty_state.assessable")}
        exportData={getAssessableExportData}
        hiddenColumns={ASSESSABLE_HIDDEN_COLUMNS}
        sectionId={sectionId}
        tableRows={assessableRows}
        title={t("assessment_dashboard.all.ready_for_assessment")}
        onRefetch={refetch}
      />

      <AssessmentStatusTable
        defaultSortField="last_assessed"
        emptyDescription={t("assessment_dashboard.all.empty_state.assessed")}
        exportData={getAssessedExportData}
        hiddenColumns={ASSESSED_HIDDEN_COLUMNS}
        sectionId={sectionId}
        tableRows={assessedRows}
        title={t("assessment_dashboard.all.assessed")}
        onRefetch={refetch}
      />

      <AssessmentStatusTable
        defaultSortDirection="asc"
        defaultSortField="due_date"
        emptyDescription={t("assessment_dashboard.all.empty_state.open_submissions")}
        exportData={openForSubmissionsExportData}
        hiddenColumns={OPEN_FOR_SUBMISSIONS_HIDDEN_COLUMNS}
        sectionId={sectionId}
        tableRows={openForSubmissionsRows}
        title={t("assessment_dashboard.all.open_for_submissions")}
        hideMarkCompleteMenu
      />
    </main>
  );
};
